import ApiClient from "@/lib/api-client"

import { isSupportedDevice, isNativePush } from "@/lib/is-platform"
import { isWithin } from "@/lib/date-utils"
import * as localStorage from "@/lib/local-storage"
import isPreviewContext from "@/lib/is-platform/is-preview-context"

// Note: Must match backend/app/models/subscriber/notification_setting.rb
export const PROVIDERS = {
  EXPO: "expo",
  PUSHER: "pusher",
  FCM: "fcm"
}
// Note: Must match backend/app/models/subscriber/notification_setting.rb
export const STATUSES = {
  ENABLED: "enabled",
  DISABLED: "disabled",
  SUSPENDED: "suspended"
}

export const NOTIFICATION_GRANTED = "granted"

const LAST_DISMISS_THRESHOLD = {
  UNIT: 3,
  INTERVAL: "day"
}

export default {
  namespaced: true,

  state: {
    receiveBlastNotifications: true,
    receiveMessageNotifications: true,
    receiveReminders: true,
    canAsk: false,
    status: null,
    token: null,
    lastAskedForPermission: localStorage.get(
      localStorage.LS_KEYS.ASKED_PUSH_PERMISSION
    ),
    provider: null, // will be set in fetchPushSettings
    pusherDeviceId: null
  },

  mutations: {
    setCanAsk(state, canAsk) {
      state.canAsk = canAsk
    },

    setStatus(state, status) {
      state.status = status
    },

    setToken(state, token) {
      state.token = token
    },

    setProvider(state, provider) {
      state.provider = provider
    },

    setPusherDeviceId(state, pusherDeviceId) {
      state.pusherDeviceId = pusherDeviceId
    },

    setSettings(
      state,
      {
        receiveBlastNotifications,
        receiveMessageNotifications,
        receiveReminders
      }
    ) {
      state.receiveBlastNotifications = receiveBlastNotifications
      state.receiveMessageNotifications = receiveMessageNotifications
      state.receiveReminders = receiveReminders
    },

    setLastAskedForPermission(state, lastAskedForPermission = +new Date()) {
      localStorage.set(
        localStorage.LS_KEYS.ASKED_PUSH_PERMISSION,
        lastAskedForPermission
      )
      state.lastAskedForPermission = lastAskedForPermission
    }
  },

  actions: {
    updatePushEndpointFromState({ state }) {
      ApiClient.put("/notifications/push", {
        token: state.token,
        provider: state.provider,
        status: state.status,
        pusher_device_id: state.pusherDeviceId
      })
    },

    fetchPushSettings({ commit }) {
      return ApiClient.get("/notifications/push_settings").then(res => {
        commit("setStatus", res.data.push_status)
        commit("setProvider", res.data.provider)
      })
    },

    fetchNotificationSettings({ commit }) {
      return ApiClient.get("/notifications").then(res => {
        commit("setSettings", {
          receiveBlastNotifications: res.data.receive_blast_notifications,
          receiveMessageNotifications: res.data.receive_message_notifications,
          receiveReminders: res.data.receive_reminders
        })
      })
    },

    updateNotificationSettings(
      { commit },
      {
        receiveBlastNotifications,
        receiveMessageNotifications,
        receiveReminders
      }
    ) {
      commit("setSettings", {
        receiveBlastNotifications,
        receiveMessageNotifications,
        receiveReminders
      })

      return ApiClient.put("/notifications", {
        receive_blast_notifications: receiveBlastNotifications,
        receive_message_notifications: receiveMessageNotifications,
        receive_reminders: receiveReminders
      }).then(res => {
        commit("setSettings", {
          receiveBlastNotifications: res.data.receive_blast_notifications,
          receiveMessageNotifications: res.data.receive_message_notifications,
          receiveReminders: res.data.receive_reminders
        })
      })
    },

    unsubscribeEmailNotifications(_, id) {
      return ApiClient.put(`/notifications/unsubscribe`, {
        subscriber_id: id
      }).then(res => res.data)
    }
  },

  getters: {
    hasRecentlyDismissed(state) {
      return state.lastAskedForPermission
        ? isWithin(
            parseInt(state.lastAskedForPermission),
            LAST_DISMISS_THRESHOLD.UNIT,
            LAST_DISMISS_THRESHOLD.INTERVAL
          )
        : false
    },

    canAskForPermission(state, _, __, rootGetters) {
      return (
        rootGetters["auth/isAuthenticated"] &&
        isSupportedDevice() &&
        (isPreviewContext() || isNativePush ? true : state.canAsk) &&
        !state.status === STATUSES.ENABLED
      )
    },

    shouldAskForPermission(_, getters) {
      return getters.canAskForPermission && !getters.hasRecentlyDismissed
    },

    isPushEnabled(state) {
      return () => {
        return !isSupportedDevice()
          ? false
          : isNativePush
          ? state.status === STATUSES.ENABLED
          : Notification?.permission === NOTIFICATION_GRANTED &&
            state.status === STATUSES.ENABLED
      }
    },

    isPushSuspended(state) {
      return () =>
        !isSupportedDevice()
          ? false
          : isNativePush
          ? state.status === STATUSES.SUSPENDED
          : Notification?.permission === NOTIFICATION_GRANTED &&
            state.status === STATUSES.SUSPENDED
    }
  }
}
